import { Controller } from "@hotwired/stimulus"
import { clockLabel, durationLabel, pad } from "@/utils/time_tracking_format"

// Google Calendar-style dragging on the day timeline. Three gestures on a
// stopped entry, all snapped to whole increments and all reading out the
// span they'd produce:
//   top handle    -- start earlier/later, end pinned
//   bottom handle -- end later/earlier, start pinned
//   body          -- MOVE the whole entry, duration kept
// Releasing submits the real PATCH (sessions#update), which re-renders the
// panel so the day re-lays out. A press that never moves falls through, so
// clicking an entry still just opens it in the detail pane. Running
// sessions are excluded -- no end to pull -- and converted ones never
// reach the panel.
export default class extends Controller {
  static targets = ["form", "startedAt", "stoppedAt"]
  static values = {
    hourPx: Number,
    snapMinutes: { type: Number, default: 15 },
    date: String,
    path: String,
    minHeight: { type: Number, default: 30 },
    threshold: { type: Number, default: 3 }
  }

  connect() {
    this.onMove = this.onMove.bind(this)
    this.onUp = this.onUp.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
  }

  disconnect() {
    this.finish()
  }

  start(event) {
    if (event.button !== 0) return

    const handle = event.currentTarget
    // Claim the gesture: the handle sits inside the entry's link, whose own
    // mousedown would otherwise start a move.
    event.preventDefault()
    event.stopPropagation()
    this.begin(event, handle.closest(".time-tracking-block"), handle.dataset, handle.dataset.resizeEdge)
  }

  // Pressing the entry body: a drag moves the whole thing, a plain click
  // still opens the detail pane. preventDefault only suppresses the
  // browser's native link-drag and text selection -- the click that
  // follows a press-without-move is unaffected.
  startMove(event) {
    if (event.button !== 0) return
    if (!event.currentTarget.dataset.resizeSessionId) return

    event.preventDefault()
    this.begin(event, event.currentTarget, event.currentTarget.dataset, "move")
  }

  begin(event, block, data, edge) {
    this.edge = edge
    this.block = block
    this.sessionId = data.resizeSessionId
    this.originStart = Number(data.resizeStartMinutes)
    this.originEnd = Number(data.resizeEndMinutes)
    this.originY = event.clientY
    this.moved = false

    window.addEventListener("mousemove", this.onMove)
    window.addEventListener("mouseup", this.onUp)
    window.addEventListener("keydown", this.onKeydown)
  }

  onMove(event) {
    const delta = event.clientY - this.originY
    if (!this.moved && Math.abs(delta) < this.thresholdValue) return

    if (!this.moved) {
      this.moved = true
      this.element.classList.add("user-select-none")
      if (this.moving) this.block.style.cursor = "grabbing"
    }

    this.span = this.snappedSpan(delta)
    this.paint(this.span)
    this.drawLabel()
  }

  get moving() {
    return this.edge === "move"
  }

  onUp() {
    const moved = this.moved
    this.finish()
    if (!moved) return

    this.suppressNextClick()
    this.submit()
  }

  onKeydown(event) {
    if (event.key !== "Escape") return

    // Snap the entry back to where it started and drop the gesture.
    this.paint({ start: this.originStart, end: this.originEnd })
    this.moved = false
    this.finish()
  }

  // A move only slides the entry -- it keeps the height it was rendered
  // with, so a sub-minute chip holds its shape. A resize recomputes the
  // height, floored so it can't collapse out of sight mid-drag.
  paint(span) {
    this.block.style.top = `${span.start * this.pxPerMinute}px`
    if (this.moving) return

    this.block.style.height = `${Math.max((span.end - span.start) * this.pxPerMinute, this.minHeightValue)}px`
  }

  finish() {
    this.stopListening()
    this.removeLabel()
    this.element.classList.remove("user-select-none")
    if (this.block) this.block.style.cursor = ""
  }

  stopListening() {
    window.removeEventListener("mousemove", this.onMove)
    window.removeEventListener("mouseup", this.onUp)
    window.removeEventListener("keydown", this.onKeydown)
  }

  // Reads out the whole span plus its duration, pinned to the edge being
  // dragged. Lives beside the entry (not inside it) -- entries clip their
  // overflow, and a chip is too short to hold a label.
  drawLabel() {
    if (!this.label) {
      this.label = document.createElement("span")
      this.label.className = "time-tracking-resize-label"
      this.block.parentElement.appendChild(this.label)
    }

    const edgeMinutes = this.edge === "bottom" ? this.span.end : this.span.start
    this.label.style.left = this.block.style.left
    this.label.style.top = `${edgeMinutes * this.pxPerMinute}px`
    this.label.textContent =
      `${clockLabel(this.span.start)} – ${clockLabel(this.span.end)} · ${durationLabel(this.span.end - this.span.start)}`
  }

  removeLabel() {
    this.label?.remove()
    this.label = null
  }

  // A move shifts both edges and keeps its length; a resize moves only the
  // dragged edge and can't cross the other one. Either way the span keeps
  // at least one increment and stays inside the day.
  snappedSpan(delta) {
    const snap = this.snapMinutesValue
    const dayMinutes = 24 * 60
    const steps = Math.round(delta / this.pxPerMinute / snap) * snap

    if (this.moving) {
      // Duration is carried, not recomputed: the server keeps the exact
      // span (seconds included) when only a start time is sent.
      const length = this.originEnd - this.originStart
      const start = Math.min(Math.max(this.originStart + steps, 0), dayMinutes - length)
      return { start, end: start + length }
    }
    if (this.edge === "top") {
      return { start: Math.min(Math.max(this.originStart + steps, 0), this.originEnd - snap), end: this.originEnd }
    }
    return { start: this.originStart, end: Math.max(Math.min(this.originEnd + steps, dayMinutes), this.originStart + snap) }
  }

  // A move sends only the new start so the server carries the duration
  // over untouched; a resize sends both edges.
  submit() {
    this.startedAtTarget.value = this.stamp(this.span.start)
    this.stoppedAtTarget.value = this.moving ? "" : this.stamp(this.span.end)
    this.formTarget.action = `${this.pathValue}/${this.sessionId}`
    this.formTarget.requestSubmit()
  }

  stamp(minutes) {
    return `${this.dateValue}T${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
  }

  get pxPerMinute() {
    return this.hourPxValue / 60
  }

  // The mouseup that ends a drag is followed by a click; swallow it so the
  // entry's link doesn't also navigate the detail pane.
  suppressNextClick() {
    const swallow = (event) => {
      event.preventDefault()
      event.stopPropagation()
    }
    window.addEventListener("click", swallow, { capture: true, once: true })
  }
}
